import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FingerPrintIcon } from '@heroicons/react/24/outline'
import { passkeysAPI } from '../../services/api'
import { AUTH_ERROR, readAuthError } from '../../services/authErrors'
import { makePasskey, passkeysSupported, wasDismissed, wasRefusedByBrowser } from '../../services/passkeys'
import { SESSIONS_QUERY_KEY } from '../profile/sessionsQueries'
import { wasCancelled, withConfirmation } from './confirmCode'
import { SECURITY_QUERY_KEY } from './securityQueries'

const PASSKEYS_KEY = ['auth', 'passkeys']
const NAME_MAX = 60

// The server sends naive UTC (no zone); read it as UTC.
const asUtc = (value) => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)

/**
 * VH-296 — the user's passkeys on the Security page (as HubPlatform's HP-39
 * PasskeysSection): fingerprint or face as the second step, beside the
 * authenticator code, which stays.
 *
 * Adding and removing are sensitive, as every change of the second factor:
 * the server wants a fresh confirmation and `withConfirmation` asks for it.
 * Removing one ends the user's other sessions. A passkey works only on the
 * site it was made on, so one made here is for VitaHub alone.
 */
export default function PasskeysCard() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const supported = passkeysSupported()

  const query = useQuery({
    queryKey: PASSKEYS_KEY,
    queryFn: () => passkeysAPI.list().then((response) => response.data),
  })
  const items = query.data?.items || []
  const max = Number(query.data?.max) || 5
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY })
    queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEY })
    queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY })
  }

  const when = (value) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(asUtc(value))

  const explain = (error, fallbackKey) => {
    if (wasCancelled(error)) return null
    if (wasDismissed(error)) return t('passkey.card.dismissed')
    if (wasRefusedByBrowser(error)) return t('passkey.card.refused')
    const { kind } = readAuthError(error)
    if (kind === AUTH_ERROR.NETWORK) return t('secondFactor.code.unreachable')
    if (kind === AUTH_ERROR.HELD) return t('auth.loginRefused')
    if (kind === AUTH_ERROR.PASSKEY_LIMIT) return t('passkey.card.limit', { max })
    if (kind === AUTH_ERROR.AUTHENTICATOR_FIRST) return t('passkey.card.authenticatorFirst')
    if (kind === AUTH_ERROR.PASSKEY_REFUSED) return t('passkey.card.refused')
    if (kind === AUTH_ERROR.PASSKEYS_UNAVAILABLE) return t('passkey.card.unavailable')
    return t(fallbackKey)
  }
  const fail = (fallbackKey) => (error) => {
    const message = explain(error, fallbackKey)
    if (message) toast.error(message)
  }

  // --- Add ------------------------------------------------------------------
  const [name, setName] = useState('')
  const addMutation = useMutation({
    mutationFn: async (chosen) => {
      const { data } = await withConfirmation(() => passkeysAPI.addOptions())
      const credential = await makePasskey(data.options)
      return passkeysAPI.add(chosen, credential)
    },
    onSuccess: () => {
      setName('')
      toast.success(t('passkey.card.added'))
    },
    onError: fail('passkey.card.addFailed'),
    onSettled: refresh,
  })
  const submitAdd = (event) => {
    event.preventDefault()
    const chosen = name.trim()
    if (chosen) addMutation.mutate(chosen)
  }

  // --- Rename ---------------------------------------------------------------
  const [editing, setEditing] = useState(null)
  const [newName, setNewName] = useState('')
  const renameMutation = useMutation({
    mutationFn: ({ id, value }) => passkeysAPI.rename(id, value),
    onSuccess: () => setEditing(null),
    onError: fail('passkey.card.renameFailed'),
    onSettled: refresh,
  })

  // --- Remove ---------------------------------------------------------------
  const removeMutation = useMutation({
    mutationFn: (id) => withConfirmation(() => passkeysAPI.remove(id)),
    onSuccess: (response) =>
      toast.success(t('passkey.card.removed', { count: Number(response.data?.sessions_ended) || 0 })),
    onError: fail('passkey.card.removeFailed'),
    onSettled: refresh,
  })

  return (
    <section aria-labelledby="security-passkeys" className="card">
      <div className="card-header">
        <div className="flex items-center">
          <FingerPrintIcon className="mr-2 h-6 w-6 text-indigo-600" aria-hidden="true" />
          <h3 id="security-passkeys" className="text-lg font-medium text-gray-900">{t('passkey.card.title')}</h3>
        </div>
      </div>
      <div className="card-body space-y-4">
        <p className="text-sm text-gray-700">{t('passkey.card.intro')}</p>
        {query.isLoading && <div className="h-12 animate-pulse rounded-lg bg-gray-100" />}
        {query.isError && <p role="alert" className="text-sm text-red-700">{t('passkey.card.loadFailed')}</p>}
        {query.isSuccess && items.length === 0 && <p className="text-sm text-gray-500">{t('passkey.card.empty')}</p>}

        {items.length > 0 && (
          <ul aria-label={t('passkey.card.list')} className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {items.map((item) => (
              <li key={item.id} data-testid={`passkey-${item.id}`} className="space-y-2 px-3 py-3">
                {editing === item.id ? (
                  <form
                    className="flex flex-col gap-2 sm:flex-row sm:items-end"
                    onSubmit={(event) => {
                      event.preventDefault()
                      if (newName.trim()) renameMutation.mutate({ id: item.id, value: newName.trim() })
                    }}
                  >
                    <div className="flex-1">
                      <label htmlFor={`passkey-name-${item.id}`} className="form-label">
                        {t('passkey.card.nameLabel')}
                      </label>
                      <input
                        id={`passkey-name-${item.id}`}
                        value={newName}
                        maxLength={NAME_MAX}
                        onChange={(event) => setNewName(event.target.value)}
                        className="form-input"
                      />
                    </div>
                    <button type="submit" disabled={!newName.trim() || renameMutation.isPending} className="btn-primary">
                      {t('common.save')}
                    </button>
                    <button type="button" onClick={() => setEditing(null)} className="btn-secondary">
                      {t('common.cancel')}
                    </button>
                  </form>
                ) : (
                  <>
                    <p className="break-words text-sm font-medium text-gray-900">{item.name}</p>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
                      <dt className="text-gray-500">{t('passkey.card.created')}</dt>
                      <dd className="text-gray-900">{when(item.created_at)}</dd>
                      <dt className="text-gray-500">{t('passkey.card.lastUsed')}</dt>
                      <dd className="text-gray-900">
                        {item.last_used_at ? when(item.last_used_at) : t('passkey.card.neverUsed')}
                      </dd>
                    </dl>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(item.id)
                          setNewName(item.name)
                        }}
                        aria-label={t('passkey.card.renameNamed', { name: item.name })}
                        className="btn-secondary"
                      >
                        {t('passkey.card.rename')}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeMutation.mutate(item.id)}
                        disabled={removeMutation.isPending}
                        aria-label={t('passkey.card.removeNamed', { name: item.name })}
                        className="btn-secondary"
                      >
                        {t('passkey.card.remove')}
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {!supported && <p className="text-sm text-gray-500">{t('passkey.card.unsupported')}</p>}
        {supported && query.isSuccess && query.data?.available !== false && items.length < max && (
          <form onSubmit={submitAdd} className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="passkey-new-name" className="form-label">{t('passkey.card.nameLabel')}</label>
              <input
                id="passkey-new-name"
                value={name}
                maxLength={NAME_MAX}
                placeholder={t('passkey.card.namePlaceholder')}
                onChange={(event) => setName(event.target.value)}
                className="form-input"
              />
            </div>
            <button type="submit" disabled={!name.trim() || addMutation.isPending} className="btn-primary">
              {addMutation.isPending ? t('passkey.login.waiting') : t('passkey.card.add')}
            </button>
          </form>
        )}
        {query.isSuccess && query.data?.available === false && (
          <p className="text-sm text-gray-500">{t('passkey.card.unavailable')}</p>
        )}
        {items.length >= max && <p className="text-sm text-gray-500">{t('passkey.card.limit', { max })}</p>}
      </div>
    </section>
  )
}
